(() => {
  const GRID_SELECTOR = '[data-frigg-variant-grid]';
  const ITEM_SELECTOR = ':scope > [data-frigg-variant-item]';
  const PAGINATION_SELECTOR = '[data-frigg-variant-pagination]';
  const PAGE_PARAMETER = 'choice_page';

  const requestedPage = () => {
    const value = Number.parseInt(new URL(window.location.href).searchParams.get(PAGE_PARAMETER) || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const pageTokens = (currentPage, totalPages) => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const sortedPages = [...pages].filter((page) => page > 0 && page <= totalPages).sort((a, b) => a - b);
    const tokens = [];
    sortedPages.forEach((page, index) => {
      if (index > 0 && page - sortedPages[index - 1] > 1) tokens.push('ellipsis');
      tokens.push(page);
    });
    return tokens;
  };

  const button = (label, page, { current = false, disabled = false, className = '' } = {}) => {
    const element = document.createElement('button');
    element.type = 'button';
    element.className = `frigg-variant-pagination__button ${className}`.trim();
    element.textContent = label;
    element.dataset.page = String(page);
    element.disabled = disabled;
    if (current) element.setAttribute('aria-current', 'page');
    return element;
  };

  const buildPagination = (container, currentPage, totalPages, start, end, totalProducts) => {
    container.replaceChildren();

    const status = document.createElement('p');
    status.className = 'frigg-variant-pagination__status';
    status.textContent = `Showing ${start + 1}–${end} of ${totalProducts} products`;
    container.append(status);

    if (totalPages <= 1) {
      container.hidden = false;
      return;
    }

    const navigation = document.createElement('nav');
    navigation.className = 'frigg-variant-pagination__nav';
    navigation.setAttribute('aria-label', 'Product pagination');
    navigation.append(button('Previous', currentPage - 1, {
      disabled: currentPage === 1,
      className: 'frigg-variant-pagination__button--wide',
    }));

    pageTokens(currentPage, totalPages).forEach((token) => {
      if (token === 'ellipsis') {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'frigg-variant-pagination__ellipsis';
        ellipsis.textContent = '…';
        ellipsis.setAttribute('aria-hidden', 'true');
        navigation.append(ellipsis);
        return;
      }
      navigation.append(button(String(token), token, { current: token === currentPage }));
    });

    navigation.append(button('Next', currentPage + 1, {
      disabled: currentPage === totalPages,
      className: 'frigg-variant-pagination__button--wide',
    }));
    container.append(navigation);
    container.hidden = false;
  };

  const updateUrl = (page) => {
    const url = new URL(window.location.href);
    if (page === 1) url.searchParams.delete(PAGE_PARAMETER);
    else url.searchParams.set(PAGE_PARAMETER, String(page));
    window.history.pushState({ ...window.history.state, friggChoicePage: page }, '', url);
  };

  const showPage = (grid, page, { updateHistory = false, scroll = false } = {}) => {
    const items = [...grid.querySelectorAll(ITEM_SELECTOR)];
    const pageSize = Math.max(1, Number.parseInt(grid.dataset.pageSize || '24', 10));
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const start = (currentPage - 1) * pageSize;
    const end = Math.min(start + pageSize, items.length);
    const container = grid.parentElement?.querySelector(PAGINATION_SELECTOR);

    items.forEach((item, index) => {
      item.hidden = index < start || index >= end;
    });
    grid.dataset.currentPage = String(currentPage);

    if (container) {
      buildPagination(container, currentPage, totalPages, start, end, items.length);
    }
    if (updateHistory) updateUrl(currentPage);
    if (scroll) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      grid.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
  };

  const initializeGrid = (grid) => {
    if (grid.dataset.friggPaginationReady === 'true') return;
    grid.dataset.friggPaginationReady = 'true';

    const container = grid.parentElement?.querySelector(PAGINATION_SELECTOR);
    container?.addEventListener('click', (event) => {
      const pageButton = event.target.closest('button[data-page]');
      if (!pageButton || pageButton.disabled) return;
      showPage(grid, Number.parseInt(pageButton.dataset.page, 10), { updateHistory: true, scroll: true });
    });
    showPage(grid, requestedPage());
  };

  const initialize = (root = document) => {
    if (root.matches?.(GRID_SELECTOR)) initializeGrid(root);
    root.querySelectorAll?.(GRID_SELECTOR).forEach(initializeGrid);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('popstate', () => {
    document.querySelectorAll(GRID_SELECTOR).forEach((grid) => showPage(grid, requestedPage()));
  });
})();
